import { useState } from 'react';
import type { ShareContactCardProvider } from '../components/shareContactCardProvider';

/** Only the fields the gate and the card need — not the whole provider profile. */
type ProviderSubject = {
  id: string;
  companyName?: string | null;
  professionalTitle?: string | null;
  phoneNumber?: string | null;
  user?: { role?: string; email?: string | null; profilePhoto?: string | null } | null;
} | null;

type Args = {
  provider: ProviderSubject;
  /** Effective role of the viewer — a delegate acts under the owner's role. */
  role?: string;
  /** False for a delegate without operating permission. */
  canOperate: boolean;
  displayName: string;
};

/**
 * Whether the viewer may send this provider's contact card, and the modal state
 * for doing it.
 *
 * Two conditions, and the second is the one that bites: `/providers/:id` is
 * SP-only by contract, but a `service_providers` row can belong to a user of
 * another role, and offering the action there guarantees a rejected send.
 */
export function useProviderContactCardShare({ provider, role, canOperate, displayName }: Args) {
  const [open, setOpen] = useState(false);

  const canShare =
    (role === 'FINANCIAL_ADVISOR' || role === 'SERVICE_PROVIDER') &&
    canOperate &&
    provider?.user?.role === 'SERVICE_PROVIDER';

  const card: ShareContactCardProvider | null = provider
    ? {
        id: provider.id,
        displayName,
        companyName: provider.companyName,
        professionalTitle: provider.professionalTitle,
        phoneNumber: provider.phoneNumber,
        email: provider.user?.email,
        profilePhoto: provider.user?.profilePhoto,
      }
    : null;

  return { canShare, card, open, setOpen, openShare: () => setOpen(true) };
}
